// src/utils/getKarateCategory.js

import { getSettings } from "./../services/settingsServices";

let saisonActiveCache = "";
let saisonActivePromise = null;

/**
 * Chargement optionnel de la saison active depuis les settings.
 * À privilégier au lancement de l'application, mais la fonction reste compatible
 * si tu passes directement saisonActive en paramètre.
 */
export const loadKarateSeason = async () => {
  if (saisonActiveCache) return saisonActiveCache;

  if (!saisonActivePromise) {
    saisonActivePromise = getSettings()
      .then((settingsData) => {
        const saison = settingsData?.[0]?.saison_active || "";
        saisonActiveCache = saison;
        return saison;
      })
      .catch((error) => {
        console.error(
          "Erreur lors de la récupération de la saison active :",
          error,
        );
        return "";
      });
  }

  return saisonActivePromise;
};

// Chargement non bloquant au chargement du fichier
loadKarateSeason();

/**
 * Exemple :
 * "2025-2026" => 2025
 *
 * Fallback intelligent :
 * - de septembre à décembre : saison qui démarre l'année en cours
 * - de janvier à août : saison qui a démarré l'année précédente
 */
export const getSeasonStartYear = (saisonActive) => {
  const match = String(saisonActive || "").match(/(\d{4})/);

  if (match) {
    return parseInt(match[1], 10);
  }

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // janvier = 0, septembre = 8

  return currentMonth >= 8 ? currentYear : currentYear - 1;
};

const extractBirthYear = (dateOfBirth) => {
  if (!dateOfBirth) return null;

  if (typeof dateOfBirth === "number") {
    return dateOfBirth >= 1900 ? dateOfBirth : null;
  }

  const date = new Date(dateOfBirth);
  const year = date.getFullYear();

  if (!Number.isFinite(year) || Number.isNaN(year) || year < 1900) {
    return null;
  }

  return year;
};

const YOUTH_RULES = [
  { label: "Mini-Poussin", minAge: 4, maxAge: 5 },
  { label: "Poussin", minAge: 6, maxAge: 7 },
  { label: "Pupille", minAge: 8, maxAge: 9 },
  { label: "Benjamin", minAge: 10, maxAge: 11 },
  { label: "Minime", minAge: 12, maxAge: 13 },
  { label: "Cadet", minAge: 14, maxAge: 15 },
  { label: "Junior", minAge: 16, maxAge: 17 },
];

const VETERAN_RULES = [
  { label: "Vétéran 1", minAge: 35, maxAge: 45 },
  { label: "Vétéran 2", minAge: 46, maxAge: 55 },
  { label: "Vétéran 3", minAge: 56, maxAge: 65 },
  { label: "Vétéran 4", minAge: 66, maxAge: 75 },

  // Dans le tableau 2025-2026, Vétérans 5 = nés en 1949 et avant.
  // Avec une année de référence 2025, cela correspond à 76 ans et +.
  { label: "Vétéran 5", minAge: 76, maxAge: Infinity },
];

const isAgeInRule = (age, rule) => {
  return age >= rule.minAge && age <= rule.maxAge;
};

/**
 * Catégorie d'âge Karaté.
 *
 * mode:
 * - "competition" : utilise Espoirs et Vétérans
 * - "general" : les adultes restent en Sénior
 *
 * Exemples saison 2025-2026 :
 * - 2014 => Benjamin
 * - 2008 => Junior
 * - 2006 => Espoir en mode competition
 * - 1985 => Vétéran 1 en mode competition
 */
const getKarateCategory = (dateOfBirth, options = {}) => {
  const {
    saisonActive,
    mode = "competition",
    includeEspoirs,
    includeVeterans,
  } = options;

  const birthYear = extractBirthYear(dateOfBirth);

  if (!birthYear) return "Inconnu";

  const referenceYear = getSeasonStartYear(saisonActive || saisonActiveCache);

  if (!referenceYear || Number.isNaN(referenceYear)) {
    return "Inconnu";
  }

  const seasonAge = referenceYear - birthYear;

  if (seasonAge < 4) {
    return "Hors catégorie";
  }

  const youthRule = YOUTH_RULES.find((rule) => isAgeInRule(seasonAge, rule));

  if (youthRule) {
    return youthRule.label;
  }

  const shouldUseEspoirs =
    typeof includeEspoirs === "boolean"
      ? includeEspoirs
      : mode === "competition";

  const shouldUseVeterans =
    typeof includeVeterans === "boolean"
      ? includeVeterans
      : mode === "competition";

  if (shouldUseVeterans) {
    const veteranRule = VETERAN_RULES.find((rule) =>
      isAgeInRule(seasonAge, rule),
    );

    if (veteranRule) {
      return veteranRule.label;
    }
  }

  if (shouldUseEspoirs && seasonAge >= 18 && seasonAge <= 20) {
    return "Espoir";
  }

  if (seasonAge >= 18) {
    return "Sénior";
  }

  return "Inconnu";
};

/**
 * Version async si tu veux être sûr d'avoir chargé la saison active
 * avant de calculer la catégorie.
 */
export const getKarateCategoryAsync = async (dateOfBirth, options = {}) => {
  const resolvedSaisonActive =
    options.saisonActive || (await loadKarateSeason());

  return getKarateCategory(dateOfBirth, {
    ...options,
    saisonActive: resolvedSaisonActive,
  });
};

export default getKarateCategory;
